/**
 * EntitiesContext provides access to all entities in the system.
 * Acts as a lightweight registry for entity metadata used throughout the app.
 */
import { createContext } from 'react';
import { Entity } from '#types/System.types';

/**
 * Context value interface for entities registry.
 * Provides access to all entities loaded from Firestore.
 */
export interface EntitiesContextValue {
  /**
   * All entities keyed by docId for O(1) lookup.
   * Used when you need to find a specific entity by ID.
   */
  entities: Record<string, Entity>;
  
  /**
   * All entities as an array for iteration.
   * Used when you need to list or filter entities.
   */
  entitiesArray: Entity[];
  
  /**
   * Loading state indicating whether entities are being fetched from Firestore.
   */
  isLoading: boolean;
  
  /**
   * Error state containing any error that occurred during loading.
   */
  error: Error | null;
}

/**
 * React context for entities registry.
 * Provides app-wide access to entity definitions for labels, metadata, and structure.
 */
export const EntitiesContext = createContext<EntitiesContextValue>({
  entities: {},
  entitiesArray: [],
  isLoading: true,
  error: null,
});

/**
 * Type export for the entities context value interface.
 * Used by components that consume the EntitiesContext.
 */
export type { EntitiesContextValue as EntitiesContextValueType };

